
import React from 'react';
import { Phone, MessageCircle, Mail } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/components/ui/use-toast";

interface ContactButtonsProps {
  phoneNumber?: string | null;
  email?: string | null;
  name?: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  onlyPhone?: boolean;
}

export const ContactButtons: React.FC<ContactButtonsProps> = ({
  phoneNumber,
  email,
  name = 'Contact',
  size = 'md',
  showLabel = false,
  onlyPhone = false
}) => {
  const iconSizes = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-6 w-6"
  };
  
  const handleCallContact = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    } else {
      toast({
        description: "No phone number available",
        variant: "destructive"
      });
    }
  };

  const handleSMS = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (phoneNumber) {
      window.location.href = `sms:${phoneNumber}`;
    } else {
      toast({
        description: "No phone number available",
        variant: "destructive"
      });
    }
  };

  const handleViber = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (phoneNumber) {
      window.location.href = `viber://chat?number=${phoneNumber?.replace(/\+/g, '')}`;
    } else {
      toast({
        description: "No phone number available",
        variant: "destructive"
      });
    }
  };

  const handleWhatsApp = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (phoneNumber) {
      window.open(`https://wa.me/${phoneNumber?.replace(/\+/g, '')}`, '_blank');
    } else {
      toast({
        description: "No phone number available",
        variant: "destructive"
      });
    }
  };

  const handleEmail = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (email) {
      window.location.href = `mailto:${email}`;
    } else {
      toast({
        description: "No email address available",
        variant: "destructive"
      });
    }
  };

  // If onlyPhone is true, only render the phone button
  if (onlyPhone) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={handleCallContact}
              className="flex items-center gap-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-md transition-colors"
            >
              <Phone className={`${iconSizes[size]} text-white`} />
              <span className="ml-1">Call {name}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Call {name}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="flex items-center gap-1 sm:gap-2">
      {phoneNumber && (
        <>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleCallContact}
                  className="hover:text-primary-600 transition-colors"
                >
                  <Phone className={`${iconSizes[size]} text-green-500 hover:text-primary-600 transition-colors`} />
                  {showLabel && <span className="ml-1">Call</span>}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Call {name}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleSMS}
                  className="hover:text-primary-600 transition-colors"
                >
                  <MessageCircle className={`${iconSizes[size]} text-blue-500 hover:text-primary-600 transition-colors`} />
                  {showLabel && <span className="ml-1">SMS</span>}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>SMS to {name}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleViber}
                  className="hover:text-primary-600 transition-colors"
                >
                  <MessageCircle className={`${iconSizes[size]} text-purple-500 hover:text-primary-600 transition-colors`} />
                  {showLabel && <span className="ml-1">Viber</span>}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Viber chat with {name}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleWhatsApp}
                  className="hover:text-primary-600 transition-colors"
                >
                  <MessageCircle className={`${iconSizes[size]} text-green-600 hover:text-primary-600 transition-colors`} />
                  {showLabel && <span className="ml-1">WhatsApp</span>}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>WhatsApp message to {name}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </>
      )}
      
      {email && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleEmail}
                className="hover:text-primary-600 transition-colors"
              >
                <Mail className={`${iconSizes[size]} text-blue-600 hover:text-primary-600 transition-colors`} />
                {showLabel && <span className="ml-1">Email</span>}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Email {name}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};
